import { useEffect, useRef, useState, useCallback } from "react";
import useAxios from "../../../../MainComponents/Hooks/useAxios";
import VideoTimedQuizModal from "../components/VideoTimedQuizModal";

export default function NvcryptStatus({
  videoId,
  history_id,
  videoRef,
  isAPIReady,
}) {
  const counterRef = useRef(0);
  const intervalRef = useRef(null);
  const playerInitializedRef = useRef(false);
  const playerInstanceRef = useRef(null);
  const lockingRef = useRef(false);

  const [submitDecrease, setSubmitDecrease] = useState({
    flag: "",
    dependency: false,
    data: {},
  });
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [unlockedUntil, setUnlockedUntil] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);

  const [success] = useAxios(
    process.env.REACT_APP_STUDENT_DECREASE_WATCHE_IN_COURSE_API,
    "POST",
    submitDecrease.flag,
    submitDecrease.dependency,
    submitDecrease.data
  );

  const [timedSuccess] = useAxios(
    videoId
      ? `${process.env.REACT_APP_STUDENT_VIDEO_TIMED_QUESTIONS_API}${videoId}/timed-questions`
      : "",
    "GET",
    videoId ? "GET" : "",
    videoId
  );

  useEffect(() => {
    if (success) {
      setSubmitDecrease({ flag: "", dependency: false, data: {} });
    }
  }, [success]);

  useEffect(() => {
    if (timedSuccess?.enable_timed_questions) {
      setCheckpoints(timedSuccess.data || []);
      setUnlockedUntil(
        timedSuccess.unlocked_until_sec === null ||
          timedSuccess.unlocked_until_sec === undefined
          ? null
          : Number(timedSuccess.unlocked_until_sec)
      );
    } else {
      setCheckpoints([]);
      setUnlockedUntil(null);
    }
  }, [timedSuccess]);

  const stopCounting = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startCounting = useCallback(() => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      counterRef.current += 1;

      if (counterRef.current === 600) {
        setSubmitDecrease((prev) => ({
          flag: "decreaseWatch",
          dependency: !prev.dependency,
          data: { id: videoId, history_id: history_id },
        }));

        counterRef.current = 0;
      }
    }, 1000);
  }, [videoId, history_id]);

  useEffect(() => {
    if (!videoRef || !isAPIReady) return;
    if (!window.NVCryptPlayer) return;
    if (playerInitializedRef.current) return;

    const player = window.NVCryptPlayer.getInstance(videoRef);
    if (!player) return;

    playerInstanceRef.current = player;

    const onTimeUpdate = () => {
      if (!timedSuccess?.enable_timed_questions) return;
      const video = player.video;
      if (!video) return;
      const t = video.currentTime || 0;
      const unanswered = checkpoints.filter((q) => !q.is_answered);
      const next = unanswered.find((q) => t >= q.timestamp_sec - 0.25);
      if (next && !lockingRef.current && !activeQuestion) {
        lockingRef.current = true;
        video.pause();
        setActiveQuestion(next);
      }
      if (
        unlockedUntil !== null &&
        unlockedUntil !== undefined &&
        t > unlockedUntil + 0.5
      ) {
        video.currentTime = Math.max(unlockedUntil - 0.2, 0);
      }
    };

    const onSeeking = () => {
      const video = player.video;
      if (!video) return;
      if (
        unlockedUntil !== null &&
        unlockedUntil !== undefined &&
        video.currentTime > unlockedUntil + 0.35
      ) {
        video.currentTime = Math.max(unlockedUntil - 0.2, 0);
      }
    };

    player.on("ready", () => {
      playerInitializedRef.current = true;

      const video = player.video;
      if (!video) return;

      video.addEventListener("play", startCounting);
      video.addEventListener("pause", stopCounting);
      video.addEventListener("ended", stopCounting);
      video.addEventListener("timeupdate", onTimeUpdate);
      video.addEventListener("seeking", onSeeking);

      if (!video.paused && !video.ended) {
        startCounting();
      }
    });

    return () => {
      stopCounting();

      const video = playerInstanceRef.current?.video;
      if (video) {
        video.removeEventListener("play", startCounting);
        video.removeEventListener("pause", stopCounting);
        video.removeEventListener("ended", stopCounting);
        video.removeEventListener("timeupdate", onTimeUpdate);
        video.removeEventListener("seeking", onSeeking);
      }

      playerInitializedRef.current = false;
      playerInstanceRef.current = null;
    };
  }, [
    videoRef,
    isAPIReady,
    startCounting,
    stopCounting,
    timedSuccess,
    checkpoints,
    unlockedUntil,
    activeQuestion,
  ]);

  const handleCorrect = (nextUnlock) => {
    if (activeQuestion) {
      setCheckpoints((prev) =>
        prev.map((q) =>
          q.id === activeQuestion.id ? { ...q, is_answered: 1 } : q
        )
      );
    }
    setActiveQuestion(null);
    lockingRef.current = false;
    setUnlockedUntil(
      nextUnlock === null || nextUnlock === undefined
        ? null
        : Number(nextUnlock)
    );
    const video = playerInstanceRef.current?.video;
    if (video) {
      try {
        video.play();
      } catch (e) {}
    }
  };

  return (
    <VideoTimedQuizModal
      open={!!activeQuestion}
      question={activeQuestion}
      onCorrect={handleCorrect}
    />
  );
}
